// Firestore操作のユーティリティ関数

import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
  where,
  Timestamp,
  getDoc,
  setDoc,
  increment,
  startAfter,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from './config';
import {
  CustomerData,
  CustomerRecord,
  ContactPerson,
  CustomerSession,
  AccessLog,
  AdminUser,
  CompanySearchCache,
} from './types';

// コレクション名のプレフィックス（環境変数から読み込み）
// Preview Channelでは 'preview_' を設定して本番データと分離
// 本番環境では空文字（プレフィックスなし）
// 注意: このプレフィックスはビルド時に環境変数から読み込まれるため、
// CI/CDワークフローで環境変数を設定する必要があります
const COLLECTION_PREFIX =
  process.env.REACT_APP_FIREBASE_COLLECTION_PREFIX || '';

// コレクション名（プレフィックス付き）
// 例: 本番環境では 'customers'、Preview Channelでは 'preview_customers'
const CUSTOMERS_COLLECTION = `${COLLECTION_PREFIX}customers`;
const CONTACT_PERSONS_COLLECTION = `${COLLECTION_PREFIX}contactPersons`;
const CUSTOMER_SESSIONS_COLLECTION = `${COLLECTION_PREFIX}customerSessions`;
const ACCESS_LOGS_COLLECTION = `${COLLECTION_PREFIX}accessLogs`;
const ADMIN_USERS_COLLECTION = `${COLLECTION_PREFIX}adminUsers`;
const COMPANY_SEARCH_CACHE_COLLECTION = `${COLLECTION_PREFIX}companySearchCache`;

// 半角文字を全角文字に変換する関数
const convertHalfWidthToFullWidth = (text: string): string => {
  // ASCII記号の全角変換マップ
  const asciiToZenkakuMap: Record<string, string> = {
    '!': '！',
    '"': '"',
    '#': '#',
    $: '＄',
    '%': '％',
    '&': '＆',
    "'": "'",
    '(': '（',
    ')': '）',
    '*': '＊',
    '+': '＋',
    ',': '，',
    '-': '－',
    '.': '．',
    '/': '／',
    ':': '：',
    ';': '；',
    '<': '＜',
    '=': '＝',
    '>': '＞',
    '?': '？',
    '@': '＠',
    '[': '［',
    '\\': '＼',
    ']': '］',
    '^': '＾',
    _: '＿',
    '`': '｀',
    '{': '｛',
    '|': '｜',
    '}': '｝',
    '~': '～',
  };

  let result = '';
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const code = char.charCodeAt(0);

    // 半角英字 A-Z (0x41-0x5A) → 全角英字 Ａ-Ｚ (0xFF21-0xFF3A)
    if (code >= 0x41 && code <= 0x5a) {
      result += String.fromCharCode(0xff21 + (code - 0x41));
    }
    // 半角英字 a-z (0x61-0x7A) → 全角英字 ａ-ｚ (0xFF41-0xFF5A)
    else if (code >= 0x61 && code <= 0x7a) {
      result += String.fromCharCode(0xff41 + (code - 0x61));
    }
    // 半角数字 0-9 (0x30-0x39) → 全角数字 ０-９ (0xFF10-0xFF19)
    else if (code >= 0x30 && code <= 0x39) {
      result += String.fromCharCode(0xff10 + (code - 0x30));
    }
    // 半角スペース (0x20) → 全角スペース (0x3000)
    else if (code === 0x20) {
      result += String.fromCharCode(0x3000);
    }
    // 半角記号 → 全角記号に変換
    else if (asciiToZenkakuMap[char] !== undefined) {
      result += asciiToZenkakuMap[char];
    }
    // その他の文字（日本語、全角英数字、全角スペースなど）はそのまま
    else {
      result += char;
    }
  }
  return result;
};

// 会社名を正規化する関数（検索用）
// - 小文字化（英語の場合）
// - 全角半角の統一（全角に統一）
const normalizeCompanyNameForSearch = (text: string): string => {
  // 全角半角を統一（全角に変換）
  const fullWidth = convertHalfWidthToFullWidth(text);
  // 小文字化（英語の場合）
  // 注意: 日本語は小文字化の影響を受けない
  let normalized = '';
  for (let i = 0; i < fullWidth.length; i++) {
    const char = fullWidth[i];
    const code = char.charCodeAt(0);
    // 全角英字 Ａ-Ｚ (0xFF21-0xFF3A) → 全角英字 ａ-ｚ (0xFF41-0xFF5A)
    if (code >= 0xff21 && code <= 0xff3a) {
      normalized += String.fromCharCode(0xff41 + (code - 0xff21));
    } else {
      normalized += char;
    }
  }
  return normalized;
};

// 次の表示用IDを取得（既存の最大値+1）
const getNextDisplayId = async (): Promise<number> => {
  try {
    const q = query(
      collection(db, CUSTOMERS_COLLECTION),
      orderBy('displayId', 'desc'),
      limit(1)
    );

    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      return 1; // 最初のID
    }

    const maxDisplayId = querySnapshot.docs[0].data().displayId || 0;
    return maxDisplayId + 1;
  } catch (error) {
    // displayIdでソートできない場合（インデックスがない、またはdisplayIdフィールドがない）は1から開始
    // eslint-disable-next-line no-console
    console.warn('Failed to get next displayId, starting from 1:', error);
    return 1;
  }
};

// 顧客データを保存
export const saveCustomerData = async (
  customerData: CustomerData | Omit<CustomerData, 'createdAt' | 'updatedAt'>
): Promise<string> => {
  // eslint-disable-next-line no-console
  console.log(`[saveCustomerData] 開始: コレクション=${CUSTOMERS_COLLECTION}`);

  try {
    // createdAt/updatedAtがDateオブジェクトの場合は削除（FirestoreでTimestampに変換するため）
    const dataWithoutDates = { ...customerData };
    if ('createdAt' in dataWithoutDates) {
      delete (dataWithoutDates as any).createdAt;
    }
    if ('updatedAt' in dataWithoutDates) {
      delete (dataWithoutDates as any).updatedAt;
    }

    // Firestoreはundefinedの値を許可しないため、undefinedのフィールドを削除
    const dataWithoutUndefined: Record<string, any> = {};
    for (const [key, value] of Object.entries(dataWithoutDates)) {
      if (value !== undefined) {
        dataWithoutUndefined[key] = value;
      }
    }

    // displayIdが存在しない場合は自動生成
    let displayId = dataWithoutUndefined.displayId;
    if (!displayId) {
      displayId = await getNextDisplayId();
    }
    // statusが存在しない場合はデフォルトで'uncontracted'（未契約）を設定
    const status = dataWithoutUndefined.status || 'uncontracted';

    // companyNameLowerを自動生成（検索用）
    const companyName = dataWithoutUndefined.companyName || '';
    const companyNameLower = normalizeCompanyNameForSearch(companyName);

    // データを準備（Timestamp付き）
    const dataToSave = {
      ...dataWithoutUndefined,
      displayId,
      status, // デフォルトで'uncontracted'（未契約）を設定
      companyNameLower, // 検索用の正規化された会社名
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    // eslint-disable-next-line no-console
    console.log(`[saveCustomerData] データ準備完了:`, {
      collection: CUSTOMERS_COLLECTION,
      dataKeys: Object.keys(dataToSave),
    });

    // Firestoreに保存
    const collectionRef = collection(db, CUSTOMERS_COLLECTION);

    // eslint-disable-next-line no-console
    console.log(`[saveCustomerData] addDoc呼び出し前`);

    // タイムアウト対策: 30秒でタイムアウト
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(
          new Error(
            `Firestore addDoc timeout after 30 seconds (collection: ${CUSTOMERS_COLLECTION})`
          )
        );
      }, 30000);
    });

    const addDocPromise = addDoc(collectionRef, dataToSave);
    const docRef = await Promise.race([addDocPromise, timeoutPromise]);

    // eslint-disable-next-line no-console
    console.log(`[saveCustomerData] addDoc完了: docId=${docRef.id}`);

    return docRef.id;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      `[saveCustomerData] エラー: コレクション=${CUSTOMERS_COLLECTION}`,
      error
    );

    if (error instanceof Error) {
      // eslint-disable-next-line no-console
      console.error(`[saveCustomerData] エラー詳細:`, {
        message: error.message,
        name: error.name,
        stack: error.stack,
      });
    }

    throw error;
  }
};

// 顧客データを更新
export const updateCustomerData = async (
  id: string,
  customerData: Partial<CustomerData>
): Promise<void> => {
  try {
    const docRef = doc(db, CUSTOMERS_COLLECTION, id);

    // companyNameが変更された場合、companyNameLowerも更新
    const updateData: Record<string, any> = {
      ...customerData,
      updatedAt: Timestamp.now(),
    };

    if (customerData.companyName !== undefined) {
      updateData.companyNameLower = normalizeCompanyNameForSearch(
        customerData.companyName
      );
    }

    await updateDoc(docRef, updateData);
  } catch (error) {
    // console.error('Error updating customer data:', error);
    throw error;
  }
};

// 顧客データを取得（一覧）
export const getCustomers = async (
  limitCount: number = 50,
  startAfterDoc?: QueryDocumentSnapshot
): Promise<{
  customers: CustomerRecord[];
  lastDoc: QueryDocumentSnapshot | null;
}> => {
  try {
    let q = query(
      collection(db, CUSTOMERS_COLLECTION),
      orderBy('displayId', 'asc'),
      limit(limitCount)
    );

    if (startAfterDoc) {
      q = query(
        collection(db, CUSTOMERS_COLLECTION),
        orderBy('displayId', 'asc'),
        startAfter(startAfterDoc),
        limit(limitCount)
      );
    }

    const querySnapshot = await getDocs(q);
    const customers: CustomerRecord[] = [];
    let lastDoc: QueryDocumentSnapshot | null = null;

    querySnapshot.forEach(doc => {
      const data = doc.data();
      customers.push({
        id: doc.id,
        customerData: {
          ...data,
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
          displayId: data.displayId || undefined,
          status: data.status || 'uncontracted', // デフォルトで'uncontracted'（未契約）
        } as CustomerData,
        calculatedResult: {
          finalRatio: 0, // 計算ロジックで更新
          difficulty: '',
          difficultyClass: '',
          requiredApplicants: 0,
          costPerHire: 0,
        },
        timestamp: data.createdAt.toDate(),
      });
      lastDoc = doc;
    });

    return { customers, lastDoc };
  } catch (error) {
    // console.error('Error getting customers:', error);
    throw error;
  }
};

// 顧客データを取得（ID指定）
export const getCustomerById = async (
  id: string
): Promise<CustomerRecord | null> => {
  try {
    const docRef = doc(db, CUSTOMERS_COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    return {
      id: docSnap.id,
      customerData: {
        ...data,
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
        displayId: data.displayId || undefined,
        status: data.status || 'uncontracted', // デフォルトで'uncontracted'（未契約）
      } as CustomerData,
      calculatedResult: {
        finalRatio: 0, // 計算ロジックで更新
        difficulty: '',
        difficultyClass: '',
        requiredApplicants: 0,
        costPerHire: 0,
      },
      timestamp: data.createdAt.toDate(),
    };
  } catch (error) {
    throw error;
  }
};

// 法人番号で企業を検索
export const findCustomerByCorporateNumber = async (
  corporateNumber: string
): Promise<CustomerData | null> => {
  try {
    const q = query(
      collection(db, CUSTOMERS_COLLECTION),
      where('corporateNumber', '==', corporateNumber),
      limit(1)
    );

    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate(),
    } as CustomerData;
  } catch (error) {
    throw error;
  }
};

// 法人番号がなく、会社名が完全一致する企業を検索
export const findCustomerByNameWithoutCorporateNumber = async (
  companyName: string
): Promise<CustomerData | null> => {
  try {
    // 法人番号がない（nullまたはundefined）かつ会社名が完全一致する企業を検索
    // 注意: Firestoreではnullの検索が難しいため、まず会社名で検索してからフィルタリング
    const q = query(
      collection(db, CUSTOMERS_COLLECTION),
      where('companyName', '==', companyName),
      limit(20) // 複数ヒットする可能性があるため、多めに取得
    );

    const querySnapshot = await getDocs(q);

    // 法人番号がない（null、undefined、または空文字）ものを探す
    for (const doc of querySnapshot.docs) {
      const data = doc.data();
      const corporateNumber = data.corporateNumber;

      // 法人番号がない場合
      if (!corporateNumber || corporateNumber === '') {
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
        } as CustomerData;
      }
    }

    return null;
  } catch (error) {
    throw error;
  }
};

// 会社名で検索（部分一致、既存の関数）
export const searchCustomersByCompany = async (
  companyName: string
): Promise<CustomerRecord[]> => {
  try {
    const q = query(
      collection(db, CUSTOMERS_COLLECTION),
      where('companyName', '>=', companyName),
      where('companyName', '<=', companyName + '\uf8ff'),
      orderBy('companyName'),
      limit(20)
    );

    const querySnapshot = await getDocs(q);
    const customers: CustomerRecord[] = [];

    querySnapshot.forEach(doc => {
      customers.push({
        id: doc.id,
        customerData: doc.data() as CustomerData,
        calculatedResult: {
          finalRatio: 0,
          difficulty: '',
          difficultyClass: '',
          requiredApplicants: 0,
          costPerHire: 0,
        },
        timestamp: doc.data().createdAt.toDate(),
      });
    });

    return customers;
  } catch (error) {
    // console.error('Error searching customers:', error);
    throw error;
  }
};

// 顧客データを削除
export const deleteCustomer = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, CUSTOMERS_COLLECTION, id);
    await deleteDoc(docRef);
  } catch (error) {
    // console.error('Error deleting customer:', error);
    throw error;
  }
};

// ===== ContactPersons関連の関数 =====

// 担当者情報を保存
export const saveContactPerson = async (
  contactPerson: Omit<ContactPerson, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  try {
    const now = Timestamp.now();
    const docRef = await addDoc(collection(db, CONTACT_PERSONS_COLLECTION), {
      ...contactPerson,
      firstInputAt: contactPerson.firstInputAt
        ? Timestamp.fromDate(contactPerson.firstInputAt)
        : now,
      lastInputAt: contactPerson.lastInputAt
        ? Timestamp.fromDate(contactPerson.lastInputAt)
        : now,
      createdAt: now,
      updatedAt: now,
    });
    return docRef.id;
  } catch (error) {
    // エラーログを出力（デバッグのため）
    // eslint-disable-next-line no-console
    console.error(
      `Error saving contact person to ${CONTACT_PERSONS_COLLECTION}:`,
      error
    );
    throw error;
  }
};

// 担当者情報を更新
export const updateContactPerson = async (
  id: string,
  contactPerson: Partial<ContactPerson>
): Promise<void> => {
  // eslint-disable-next-line no-console
  console.log(
    `[updateContactPerson] 開始: id=${id}, コレクション=${CONTACT_PERSONS_COLLECTION}`
  );

  try {
    const docRef = doc(db, CONTACT_PERSONS_COLLECTION, id);
    const updateData: any = {
      ...contactPerson,
      updatedAt: Timestamp.now(),
    };
    if (contactPerson.firstInputAt) {
      updateData.firstInputAt = Timestamp.fromDate(contactPerson.firstInputAt);
    }
    if (contactPerson.lastInputAt) {
      updateData.lastInputAt = Timestamp.fromDate(contactPerson.lastInputAt);
    }

    // eslint-disable-next-line no-console
    console.log(`[updateContactPerson] データ準備完了:`, {
      collection: CONTACT_PERSONS_COLLECTION,
      id,
      updateKeys: Object.keys(updateData),
    });

    // eslint-disable-next-line no-console
    console.log(`[updateContactPerson] updateDoc呼び出し前`);

    // タイムアウト対策: 30秒でタイムアウト
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(
          new Error(
            `Firestore updateDoc timeout after 30 seconds (collection: ${CONTACT_PERSONS_COLLECTION}, id: ${id})`
          )
        );
      }, 30000);
    });

    const updateDocPromise = updateDoc(docRef, updateData);
    await Promise.race([updateDocPromise, timeoutPromise]);

    // eslint-disable-next-line no-console
    console.log(`[updateContactPerson] updateDoc完了: id=${id}`);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      `[updateContactPerson] エラー: コレクション=${CONTACT_PERSONS_COLLECTION}, id=${id}`,
      error
    );

    if (error instanceof Error) {
      // eslint-disable-next-line no-console
      console.error(`[updateContactPerson] エラー詳細:`, {
        message: error.message,
        name: error.name,
        stack: error.stack,
      });
    }

    throw error;
  }
};

// 企業IDとemailで担当者を検索
export const findContactPersonByCompanyIdAndEmail = async (
  companyId: string,
  email: string
): Promise<ContactPerson | null> => {
  try {
    const q = query(
      collection(db, CONTACT_PERSONS_COLLECTION),
      where('companyId', '==', companyId),
      where('email', '==', email),
      limit(1)
    );

    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    const data = doc.data();
    return {
      id: doc.id,
      companyId: data.companyId,
      name: data.name,
      email: data.email,
      phone: data.phone,
      position: data.position,
      firstInputAt: data.firstInputAt.toDate(),
      lastInputAt: data.lastInputAt.toDate(),
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate(),
    };
  } catch (error) {
    throw error;
  }
};

// 企業IDで担当者一覧を取得
export const getContactPersonsByCompanyId = async (
  companyId: string
): Promise<ContactPerson[]> => {
  try {
    const q = query(
      collection(db, CONTACT_PERSONS_COLLECTION),
      where('companyId', '==', companyId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const contactPersons: ContactPerson[] = [];

    querySnapshot.forEach(doc => {
      const data = doc.data();
      contactPersons.push({
        id: doc.id,
        companyId: data.companyId,
        name: data.name,
        email: data.email,
        phone: data.phone,
        position: data.position,
        firstInputAt: data.firstInputAt.toDate(),
        lastInputAt: data.lastInputAt.toDate(),
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
      });
    });

    return contactPersons;
  } catch (error) {
    throw error;
  }
};

// 担当者情報を取得（ID指定）
export const getContactPerson = async (
  id: string
): Promise<ContactPerson | null> => {
  try {
    const docRef = doc(db, CONTACT_PERSONS_COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    return {
      id: docSnap.id,
      companyId: data.companyId,
      name: data.name,
      email: data.email,
      phone: data.phone,
      position: data.position,
      firstInputAt: data.firstInputAt.toDate(),
      lastInputAt: data.lastInputAt.toDate(),
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate(),
    };
  } catch (error) {
    throw error;
  }
};

// ===== CustomerSessions関連の関数 =====

// 顧客セッションを保存
export const saveCustomerSession = async (
  session: Omit<CustomerSession, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  // eslint-disable-next-line no-console
  console.log(
    `[saveCustomerSession] 開始: コレクション=${CUSTOMER_SESSIONS_COLLECTION}`
  );

  try {
    const now = Timestamp.now();
    const dataToSave = {
      companyId: session.companyId,
      contactPersonId: session.contactPersonId,
      customerData: {
        ...session.customerData,
        createdAt: Timestamp.fromDate(session.customerData.createdAt),
        updatedAt: Timestamp.fromDate(session.customerData.updatedAt),
      },
      calculatedResult: session.calculatedResult,
      savedAt: Timestamp.fromDate(session.savedAt),
      createdAt: now,
      updatedAt: now,
    };

    // eslint-disable-next-line no-console
    console.log(`[saveCustomerSession] データ準備完了:`, {
      collection: CUSTOMER_SESSIONS_COLLECTION,
      companyId: session.companyId,
      contactPersonId: session.contactPersonId,
    });

    const collectionRef = collection(db, CUSTOMER_SESSIONS_COLLECTION);

    // eslint-disable-next-line no-console
    console.log(`[saveCustomerSession] addDoc呼び出し前`);

    // タイムアウト対策: 30秒でタイムアウト
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(
          new Error(
            `Firestore addDoc timeout after 30 seconds (collection: ${CUSTOMER_SESSIONS_COLLECTION})`
          )
        );
      }, 30000);
    });

    const addDocPromise = addDoc(collectionRef, dataToSave);
    const docRef = await Promise.race([addDocPromise, timeoutPromise]);

    // eslint-disable-next-line no-console
    console.log(`[saveCustomerSession] addDoc完了: docId=${docRef.id}`);

    return docRef.id;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      `[saveCustomerSession] エラー: コレクション=${CUSTOMER_SESSIONS_COLLECTION}`,
      error
    );

    if (error instanceof Error) {
      // eslint-disable-next-line no-console
      console.error(`[saveCustomerSession] エラー詳細:`, {
        message: error.message,
        name: error.name,
        stack: error.stack,
      });
    }

    throw error;
  }
};

// 担当者IDで顧客セッション一覧を取得（降順）
export const getCustomerSessionsByContactPersonId = async (
  contactPersonId: string,
  limitCount: number = 100
): Promise<CustomerSession[]> => {
  try {
    const q = query(
      collection(db, CUSTOMER_SESSIONS_COLLECTION),
      where('contactPersonId', '==', contactPersonId),
      orderBy('savedAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const sessions: CustomerSession[] = [];

    querySnapshot.forEach(doc => {
      const data = doc.data();
      const customerData = data.customerData;
      sessions.push({
        id: doc.id,
        companyId: data.companyId,
        contactPersonId: data.contactPersonId,
        customerData: {
          ...customerData,
          createdAt: customerData.createdAt.toDate(),
          updatedAt: customerData.updatedAt.toDate(),
        },
        calculatedResult: data.calculatedResult,
        savedAt: data.savedAt.toDate(),
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
      });
    });

    return sessions;
  } catch (error) {
    throw error;
  }
};

// 企業IDで顧客セッション一覧を取得（降順）
export const getCustomerSessionsByCompanyId = async (
  companyId: string,
  limitCount: number = 100
): Promise<CustomerSession[]> => {
  try {
    const q = query(
      collection(db, CUSTOMER_SESSIONS_COLLECTION),
      where('companyId', '==', companyId),
      orderBy('savedAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const sessions: CustomerSession[] = [];

    querySnapshot.forEach(doc => {
      const data = doc.data();
      const customerData = data.customerData;
      sessions.push({
        id: doc.id,
        companyId: data.companyId,
        contactPersonId: data.contactPersonId,
        customerData: {
          ...customerData,
          createdAt: customerData.createdAt.toDate(),
          updatedAt: customerData.updatedAt.toDate(),
        },
        calculatedResult: data.calculatedResult,
        savedAt: data.savedAt.toDate(),
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
      });
    });

    return sessions;
  } catch (error) {
    throw error;
  }
};

// ===== AccessLogs関連の関数 =====

// アクセスログを記録（日付ごとに集計）
export const logAccess = async (
  userId: string,
  companyId: string,
  action: string = 'view_dashboard'
): Promise<void> => {
  try {
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD形式
    const logId = `${userId}_${companyId}_${dateStr}`;

    const docRef = doc(db, ACCESS_LOGS_COLLECTION, logId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      // 既存のログを更新
      const existingData = docSnap.data();
      const existingActions = existingData.actions || [];
      const updatedActions = existingActions.includes(action)
        ? existingActions
        : [...existingActions, action];
      await updateDoc(docRef, {
        count: increment(1),
        lastAccessTime: Timestamp.now(),
        actions: updatedActions,
        updatedAt: Timestamp.now(),
      });
    } else {
      // 新規ログを作成
      await setDoc(docRef, {
        userId,
        companyId,
        date: dateStr,
        count: 1,
        lastAccessTime: Timestamp.now(),
        actions: [action],
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
    }
  } catch (error) {
    throw error;
  }
};

// ユーザーIDでアクセスログ一覧を取得
export const getAccessLogsByUserId = async (
  userId: string
): Promise<AccessLog[]> => {
  try {
    const q = query(
      collection(db, ACCESS_LOGS_COLLECTION),
      where('userId', '==', userId),
      orderBy('date', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const logs: AccessLog[] = [];

    querySnapshot.forEach(doc => {
      const data = doc.data();
      logs.push({
        id: doc.id,
        userId: data.userId,
        companyId: data.companyId,
        date: data.date,
        count: data.count,
        lastAccessTime: data.lastAccessTime.toDate(),
        actions: data.actions || [],
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
      });
    });

    return logs;
  } catch (error) {
    throw error;
  }
};

// 企業IDでアクセスログ一覧を取得
export const getAccessLogsByCompanyId = async (
  companyId: string
): Promise<AccessLog[]> => {
  try {
    const q = query(
      collection(db, ACCESS_LOGS_COLLECTION),
      where('companyId', '==', companyId),
      orderBy('date', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const logs: AccessLog[] = [];

    querySnapshot.forEach(doc => {
      const data = doc.data();
      logs.push({
        id: doc.id,
        userId: data.userId,
        companyId: data.companyId,
        date: data.date,
        count: data.count,
        lastAccessTime: data.lastAccessTime.toDate(),
        actions: data.actions || [],
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
      });
    });

    return logs;
  } catch (error) {
    throw error;
  }
};

// ===== CompanySearchCache関連の関数 =====

/**
 * 検索条件のハッシュキーを生成（会社名 + kind）
 */
const generateSearchCacheKey = (searchName: string, kind?: string): string => {
  const key = kind ? `${searchName}_kind_${kind}` : searchName;
  // ハッシュ化してドキュメントIDとして使用（FirestoreのドキュメントID制約に対応）
  // 簡易的なハッシュ関数（実際のプロダクションではより堅牢な方法を推奨）
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    const char = key.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return `search_${Math.abs(hash).toString(36)}`;
};

/**
 * 検索結果キャッシュを取得（1ヶ月以内のもののみ）
 */
export const getCompanySearchCache = async (
  searchName: string,
  kind?: string
): Promise<CompanySearchCache | null> => {
  try {
    const cacheKey = generateSearchCacheKey(searchName, kind);
    const docRef = doc(db, COMPANY_SEARCH_CACHE_COLLECTION, cacheKey);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    const lastSearchedAt = data.lastSearchedAt.toDate();

    // 1ヶ月（30日）以内のもののみ有効
    const oneMonthAgo = new Date();
    oneMonthAgo.setDate(oneMonthAgo.getDate() - 30);

    if (lastSearchedAt < oneMonthAgo) {
      return null; // キャッシュが古い
    }

    return {
      id: docSnap.id,
      searchName: data.searchName,
      kind: data.kind || undefined,
      results: data.results,
      lastSearchedAt: lastSearchedAt,
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate(),
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error getting search cache:', error);
    return null; // エラー時はキャッシュなしとして扱う
  }
};

/**
 * 検索結果キャッシュを保存（サーバエラー以外の結果を保存）
 */
export const saveCompanySearchCache = async (
  searchName: string,
  kind: string | undefined,
  results: {
    success: boolean;
    data?: Array<{
      corporateNumber: string;
      name: string;
      address: string;
      corporateTypeCode?: string;
    }>;
    errorCode?: string;
    message?: string;
  }
): Promise<void> => {
  try {
    // サーバエラー（success=false かつ errorCodeがない、または500エラーなど）は保存しない
    // 検索結果0件、複数件ヒット、エンコードエラーなどは保存する
    if (!results.success && !results.errorCode && !results.message) {
      // サーバエラーの可能性があるため保存しない
      return;
    }

    const cacheKey = generateSearchCacheKey(searchName, kind);
    const now = Timestamp.now();

    const cacheData = {
      searchName,
      kind: kind || null,
      results,
      lastSearchedAt: now,
      createdAt: now,
      updatedAt: now,
    };

    // 既存のドキュメントがあれば更新、なければ作成
    const docRef = doc(db, COMPANY_SEARCH_CACHE_COLLECTION, cacheKey);
    await setDoc(docRef, cacheData, { merge: true });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error saving search cache:', error);
    // キャッシュ保存の失敗は処理を続行（非同期で失敗してもインポートは継続）
  }
};

/**
 * 複数の検索条件のキャッシュを一括取得
 */
export const getCompanySearchCaches = async (
  searchQueries: Array<{ searchName: string; kind?: string }>
): Promise<Map<string, CompanySearchCache | null>> => {
  const cacheMap = new Map<string, CompanySearchCache | null>();

  // 並列でキャッシュを取得（1000件ごとにバッチ処理）
  const BATCH_SIZE = 1000;
  for (let i = 0; i < searchQueries.length; i += BATCH_SIZE) {
    const batch = searchQueries.slice(i, i + BATCH_SIZE);
    const batchPromises = batch.map(async query => {
      const cache = await getCompanySearchCache(query.searchName, query.kind);
      const key = query.kind
        ? `${query.searchName}_kind_${query.kind}`
        : query.searchName;
      return { key, cache };
    });

    const batchResults = await Promise.all(batchPromises);
    batchResults.forEach(({ key, cache }) => {
      cacheMap.set(key, cache);
    });
  }

  return cacheMap;
};

// ===== AdminUsers関連の関数 =====

// 管理ユーザー情報を取得（ID指定）
export const getAdminUser = async (id: string): Promise<AdminUser | null> => {
  try {
    const docRef = doc(db, ADMIN_USERS_COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    return {
      id: docSnap.id,
      userId: data.userId || docSnap.id, // userIdが存在しない場合はidを使用（後方互換性）
      email: data.email,
      name: data.name || undefined,
      role: data.role,
      assignedCompanies: data.assignedCompanies || [],
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate(),
    };
  } catch (error) {
    throw error;
  }
};

// 管理ユーザー一覧を取得
export const getAdminUsers = async (): Promise<AdminUser[]> => {
  try {
    const q = query(
      collection(db, ADMIN_USERS_COLLECTION),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const users: AdminUser[] = [];

    querySnapshot.forEach(doc => {
      const data = doc.data();
      users.push({
        id: doc.id,
        userId: data.userId || doc.id, // userIdが存在しない場合はidを使用（後方互換性）
        email: data.email,
        name: data.name || undefined,
        role: data.role,
        assignedCompanies: data.assignedCompanies || [],
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
      });
    });

    return users;
  } catch (error) {
    throw error;
  }
};

// 管理ユーザー情報を保存（新規作成または更新）
export const saveAdminUser = async (
  id: string,
  adminUser: Omit<AdminUser, 'id' | 'createdAt' | 'updatedAt'>
): Promise<void> => {
  try {
    const docRef = doc(db, ADMIN_USERS_COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      // 更新
      await updateDoc(docRef, {
        ...adminUser,
        updatedAt: Timestamp.now(),
      });
    } else {
      // 新規作成
      await setDoc(docRef, {
        ...adminUser,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
    }
  } catch (error) {
    throw error;
  }
};
